import { isCapacitor } from "./capacitor";
import { loadSettings } from "./settings";
import { nativePlugin } from "./nativeplugin";

/*
 * Kijken of er een nieuwere APK klaarstaat.
 *
 * De Windows-app werkt zichzelf bij - Tauri heeft daar een updater voor - maar
 * Android heeft niets van dien aard. Een nieuwe APK stond dus klaar op de server
 * zonder dat iemand het wist, tenzij hij toevallig de installatiepagina opende.
 *
 * De server publiceert al `updates/android.json` met versie en url; dat is
 * hetzelfde bestand waar de downloadpagina op leunt. Meer is er niet nodig: de
 * app kent zijn eigen versie via __APP_VERSION__, dat bij het bouwen uit
 * package.json wordt ingebakken.
 */

export type NieuweVersie = { versie: string; url: string };

/** "1.22.0" en "1.22" zijn hetzelfde; android.json schrijft de korte vorm. */
function delen(v: string): number[] {
  return v
    .trim()
    .replace(/^v/, "")
    .split(".")
    .map((d) => Number.parseInt(d, 10))
    .map((n) => (Number.isFinite(n) ? n : 0));
}

/** Puur numeriek vergelijken, deel voor deel. Een ontbrekend deel telt als 0,
 *  zodat 1.22 en 1.22.0 gelijk zijn en 1.9 ouder is dan 1.22 - wat bij een
 *  tekstvergelijking juist andersom zou uitpakken. */
export function isNieuwer(kandidaat: string, huidig: string): boolean {
  const a = delen(kandidaat);
  const b = delen(huidig);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x > y;
  }
  return false;
}

export function huidigeVersie(): string {
  return __APP_VERSION__;
}

/** null = niets nieuws, of we konden het niet weten. Beide zijn hier hetzelfde:
 *  zonder zekerheid tonen we geen balk. */
export async function zoekNieuweVersie(): Promise<NieuweVersie | null> {
  if (!isCapacitor()) return null;
  try {
    const { server_url } = loadSettings();
    if (!server_url) return null;
    const r = await fetch(`${server_url.replace(/\/$/, "")}/updates/android.json`, {
      cache: "no-store",
    });
    if (!r.ok) return null;
    const m = (await r.json()) as { version?: unknown; url?: unknown };
    if (typeof m.version !== "string" || typeof m.url !== "string") return null;
    if (!isNieuwer(m.version, huidigeVersie())) return null;
    return { versie: m.version, url: m.url };
  } catch {
    /* Geen netwerk, geen server, kapotte json - dan gewoon geen balk. */
    return null;
  }
}

interface SwiftBatteryPlugin {
  openExtern(opties: { url: string }): Promise<{ opened: boolean }>;
}

/**
 * De installatiepagina openen in de échte browser, niet in het app-venster.
 *
 * Dat moet buiten de app, om twee redenen. De app-WebView kan een bestand niet
 * downloaden, dus een venster dat naar een APK wijst blijft eindeloos laden -
 * daar liepen we op 07-09-2026 al een keer tegenaan. En Android wil een
 * installatie zien komen van een app die daar toestemming voor heeft; Chrome is
 * dat, onze WebView niet.
 */
export async function openInstallatiePagina(): Promise<boolean> {
  const url = `${loadSettings().server_url.replace(/\/$/, "")}/install/android`;
  try {
    const p = nativePlugin<SwiftBatteryPlugin>("SwiftBattery");
    const r = await p.openExtern({ url });
    if (r.opened) return true;
  } catch {
    /* Oudere APK zonder deze methode - dan de gewone weg proberen. */
  }
  window.open(url, "_blank");
  return false;
}
